import { Plans } from '@/services/billing/plans';
import { formatNumber } from '@/utils/number';

// Static comparison copy: the plan a reader is not on has no limits in the API. Priced and volume cells
// derive from the `Plans` catalog; the entitlement cells (object journeys, agent skills, routines,
// data-driven segments) match the backend Essentials feature group. NONE marks a row a plan lacks.

const ESSENTIALS = Plans.Essentials;
const PREMIUM = Plans.Premium;

const NONE = 'Not included';

export type ChangePlanRow = {
  label: string;
  essentials: string;
  premium: string;
};

export type ChangePlanColumn = {
  name: string;
  priceLabel: string;
  priceSuffix: string;
  description: string;
};

function dollars(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US')}`;
}

function perThousand(perMessageRate: string): string {
  return `$${(Number(perMessageRate) * 1000).toFixed(2)}`;
}

export const CHANGE_PLAN_COLUMNS = {
  essentials: {
    name: ESSENTIALS.name,
    priceLabel: dollars(ESSENTIALS.basePrice),
    priceSuffix: 'per month',
    description: ESSENTIALS.description,
  },
  premium: {
    name: PREMIUM.name,
    priceLabel: `${dollars(PREMIUM.basePrice)}+`,
    priceSuffix: 'per month',
    description: PREMIUM.description,
  },
} satisfies Record<'essentials' | 'premium', ChangePlanColumn>;

export const CHANGE_PLAN_ROWS: ChangePlanRow[] = [
  {
    label: 'Base price',
    essentials: `${dollars(ESSENTIALS.basePrice)} / month`,
    premium: `From ${dollars(PREMIUM.basePrice)} / month`,
  },
  {
    label: 'Profiles',
    essentials: `${formatNumber(ESSENTIALS.includedProfiles)} / month, $${ESSENTIALS.profileOverageRate} per extra profile`,
    // $0.004 per the pricing docs. The catalog Premium rate is stale, so do not derive it.
    premium: 'Custom, $0.004 per extra profile',
  },
  {
    label: 'Messages sent',
    essentials: `${formatNumber(ESSENTIALS.includedMessages)} / month, ${perThousand(ESSENTIALS.messageOverageRate)} per extra 1,000`,
    premium: `Custom email volume, ${perThousand(PREMIUM.messageOverageRate)} per extra 1,000`,
  },
  { label: 'Push, in-app, webhook, Slack', essentials: 'Unlimited', premium: 'Unlimited' },
  { label: 'SMS sends', essentials: 'Pass-through', premium: 'Pass-through' },
  { label: 'Workspaces', essentials: String(ESSENTIALS.planLimits.workspaces?.value ?? NONE), premium: 'Unlimited' },
  { label: 'Object types', essentials: String(ESSENTIALS.planLimits.objectTypes?.value ?? NONE), premium: '10' },
  { label: 'Object journeys', essentials: '1,000', premium: '5,000' },
  { label: 'Custom agent skills', essentials: '3', premium: '10' },
  { label: 'Routines', essentials: '1', premium: '5' },
  { label: 'Data-driven segments', essentials: 'Included', premium: 'Included' },
  { label: 'AI credits', essentials: '$10 per 100,000', premium: '$10 per 100,000' },
  { label: 'Premium integrations', essentials: NONE, premium: 'Included' },
  {
    label: 'Advanced features',
    essentials: NONE,
    premium:
      'Collections, geofences, warehouse sync, anonymous In-App, Live notifications, sensitive attributes, deliverability analytics, custom roles',
  },
  { label: 'Support', essentials: 'Email and community', premium: 'Email, community, live chat, CSM for onboarding' },
  { label: 'Dedicated IP', essentials: '$50 / month each', premium: '3 included, then $50 / month each' },
];
